import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaHeart, FaExternalLinkAlt } from 'react-icons/fa';
import { likeVideo, viewVideo, trackAdImpression } from '../api/videoService';
import { saveResumePosition, getResumePosition, clearResumePosition } from '../hooks/useSessionResume';
import { usePoints } from '../hooks/PointsContext';
import { useAudio } from '../hooks/AudioContext';
import { useViewTracking } from '../hooks/useViewTracking';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';

// Styled Components
const VideoContainer = styled.div.attrs({ className: 'video-container' })`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::before,
  &::after {
    display: none;
  }
`;

const VideoArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: hidden;
`;

const TopBar = styled.div`
  width: 100%;
  background: #111;
  color: white;
  padding: 8px 16px 6px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const BottomBar = styled.div`
  width: 100%;
  background: #111;
  padding: 4px 0 calc(0px + env(safe-area-inset-bottom));
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;


const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
  display: block;
  
  /* Completely hide all native video controls */
  &::-webkit-media-controls,
  &::-webkit-media-controls-panel,
  &::-webkit-media-controls-play-button,
  &::-webkit-media-controls-volume-slider,
  &::-webkit-media-controls-mute-button,
  &::-webkit-media-controls-timeline,
  &::-webkit-media-controls-current-time-display,
  &::-webkit-media-controls-time-remaining-display,
  &::-webkit-media-controls-fullscreen-button,
  &::-webkit-media-controls-overlay-enclosure,
  &::-webkit-media-controls-enclosure,
  &::-webkit-media-controls-overlay {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }
  
  &::-moz-media-controls { display: none !important; }
  &::-ms-media-controls { display: none !important; }
`;

const BufferingSpinner = styled.div`
  position: absolute;
  inset: 0;
  margin: auto;
  z-index: 5;
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  pointer-events: none;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Controls = styled.div.attrs({ className: 'video-controls' })`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 10;
  
  @media (max-width: 768px) {
    right: 5px;
    gap: 15px;
  }
`;

const ControlButton = styled.button`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  /* iPad specific styling - larger buttons for better touch targets */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait),
         (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }
  
  @media (max-width: 767px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
`;

const LikeButton = styled(ControlButton)`
  color: ${props => props.liked ? '#ff2d55' : 'white'};
`;

const VideoInfo = styled.div.attrs({ className: 'video-info' })`
  /* normal flow — no absolute positioning */
  width: 100%;
  color: white;
  text-shadow: none;
`;


const VideoTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 6px;
  }
`;

const VideoTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;


const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const PointsCost = styled.span`
  color: rgba(255,255,255,0.55);
  font-size: 24px;
  font-weight: 700;
  white-space: nowrap;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const VideoStats = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 15px;
  font-size: 14px;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    gap: 10px;
    font-size: 12px;
    flex-wrap: wrap;
  }
`;

const VideoUrl = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 20px;
  padding: 4px 10px 4px 8px;
  align-self: flex-start;
  letter-spacing: 0.01em;
  transition: background 0.15s;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const DurationRow = styled.div`
  text-align: center;
  padding: 4px 16px 4px;
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 3px;
  background-color: rgba(255, 255, 255, 0.25);
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
`;


const Progress = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff4757, #ff3742);
  width: ${props => props.progress || 0}%;
  transition: width 0.1s linear;
  border-radius: inherit;
`;


const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const PointsConfirmation = styled.div`
  position: relative;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  animation: ${props => props.isVisible ? fadeIn : fadeOut} 0.5s ease forwards;
  display: ${props => props.display ? 'block' : 'none'};
`;

const PointsAmount = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #ff4757;
  margin: 10px 0;
`;

const PointsMessage = styled.div`
  font-size: 16px;
  margin-bottom: 10px;
`;

const pulseAnimation = keyframes`
  0% { 
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
    transform: translateX(-50%) scale(1);
  }
  50% { 
    box-shadow: 0 0 0 15px rgba(255, 71, 87, 0);
    transform: translateX(-50%) scale(1.05);
  }
  100% { 
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
    transform: translateX(-50%) scale(1);
  }
`;

const TapToUnmuteMessage = styled.div`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(255, 71, 87, 0.85), rgba(255, 55, 66, 0.85));
  backdrop-filter: blur(10px);
  color: white;
  padding: 16px 24px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 700;
  z-index: 1000;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 25px rgba(255, 71, 87, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  animation: ${pulseAnimation} 2s infinite;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 55, 66, 0.9), rgba(255, 45, 57, 0.9));
    transform: translateX(-50%) scale(1.08);
    box-shadow: 0 12px 35px rgba(255, 71, 87, 0.4);
    animation: none;
  }
  
  &:active {
    transform: translateX(-50%) scale(0.98);
  }
  
  @media (max-width: 768px) {
    top: 75px;
    padding: 14px 20px;
    font-size: 15px;
    border-radius: 25px;
  }
`;

const SoundIcon = styled(FaVolumeUp)`
  font-size: 18px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const VideoPlayer = ({ video, autoPlay, inView, warmLevel = 'none', onWarmState, onVideoComplete, publicationId = '' }) => {
  const { deductPoints } = usePoints();
  const { unlockAudioContext } = useAudio();

  const videoRef = useRef(null);
  // Design 048: src is DEFERRED — attached exactly once, when the feed warms this slide (or it
  // becomes active). A fresh src assignment reliably starts a load honouring `preload` on every
  // engine incl. Safari; src is never reassigned (that would destroy the buffer).
  const srcAttachedRef = useRef(false);
  const attachedLevelRef = useRef(null);   // the preload level src was attached with (escalation, P2)
  const lastProgressAtRef = useRef(0);     // ts of the last 'progress' event (escalation fallback)
  const warmFailedRef = useRef(false); // an OFF-SCREEN load failed — retry with a fresh src when visible
  const warmLevelRef = useRef(warmLevel);
  const inViewRef = useRef(inView);
  const onWarmStateRef = useRef(onWarmState);
  // Design 048 P2: startup_ms — time from slide-visible to first 'playing'
  const inViewSinceRef = useRef(0);
  const startupSentRef = useRef(false);
  // Token invalidating pending play() retries when the slide leaves view (resurrection fix)
  const playAttemptRef = useRef(0);
  const durationRef = useRef(0); // track last-set duration without re-creating updateProgress
  const lastSavedRef = useRef(0); // last currentTime persisted to resume map
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // always start muted; effect sets real value before play
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(video.likes || 0);
  const [views] = useState(video.views || 0);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pointsDeducted, setPointsDeducted] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  
  // Points confirmation state
  const [showPointsConfirmation, setShowPointsConfirmation] = useState(false);
  const [pointsConfirmationVisible, setPointsConfirmationVisible] = useState(false);
  
  // Mobile tap to unmute state
  const [showTapToUnmute, setShowTapToUnmute] = useState(false);

  // Buffering indicator state
  const [isBuffering, setIsBuffering] = useState(false);
  // Design 048: spinner shows from inView until the FIRST 'playing' event (then only while buffering)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);

  // Keep callback/prop refs in sync at commit time (not during render — React 18 StrictMode safe)
  useLayoutEffect(() => {
    onWarmStateRef.current = onWarmState;
    inViewRef.current = inView;
  });

  // Design 048 P2: stamp when this slide became visible (startup_ms measurement anchor)
  useEffect(() => {
    if (inView) {
      inViewSinceRef.current = performance.now();
      startupSentRef.current = false;
      lastProgressAtRef.current = performance.now(); // baseline for the stall watchdog
    }
  }, [inView, video.id]);

  // Design 048 P2.1: ACTIVE-slide stall watchdog. If the slide the user is ON has produced no
  // frames (readyState < 2) and nothing has downloaded for >2.5s, reset-and-retry its load —
  // recovers a dead element (e.g. Safari ignoring a preload escalation, or a stalled fetch)
  // where load() is safe because nothing has rendered yet. Capped at 3 attempts.
  useEffect(() => {
    if (!inView) return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = videoRef.current;
      if (!el || !srcAttachedRef.current) return;
      if (el.readyState >= 2) { clearInterval(iv); return; }  // frames exist — playback path owns it
      const stalled = performance.now() - lastProgressAtRef.current > 2500;
      if (stalled && tries < 3) {
        tries += 1;
        lastProgressAtRef.current = performance.now();
        try { el.load(); } catch { /* best-effort */ }
        el.play().catch(() => {});
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [inView, video.id]);

  // Design 048: attach the source exactly once — preload attribute FIRST, then src.
  const ensureSrc = useCallback(() => {
    const el = videoRef.current;
    if (!el || srcAttachedRef.current || !video.url) return;
    const level = warmLevelRef.current !== 'none' ? warmLevelRef.current : 'auto';
    el.setAttribute('preload', level);
    el.src = video.url;
    srcAttachedRef.current = true;
    attachedLevelRef.current = level;
  }, [video.url]);

  // Design 048: health reporting — measured from the element's buffered ranges (the only signal
  // that is both universal and honest; canplaythrough is deliberately NOT used).
  const reportWarmState = useCallback(() => {
    const el = videoRef.current;
    if (!el || !onWarmStateRef.current) return;
    let bufferedAhead = 0;
    try {
      const t = el.currentTime;
      for (let i = 0; i < el.buffered.length; i++) {
        if (el.buffered.start(i) <= t + 0.1 && el.buffered.end(i) >= t) {
          bufferedAhead = el.buffered.end(i) - t;
          break;
        }
      }
    } catch { /* buffered access can throw pre-metadata */ }
    const dur = el.duration || 0;
    const wholeBuffered = dur > 0 && bufferedAhead >= dur - el.currentTime - 0.25;
    const healthy = inViewRef.current
      ? el.readyState >= 3 && (bufferedAhead >= 4 || wholeBuffered)
      : el.readyState >= 2 && (bufferedAhead >= 3 || wholeBuffered);
    onWarmStateRef.current(video.id, {
      level: warmLevelRef.current, readyState: el.readyState, bufferedAhead, healthy,
    });
  }, [video.id]);

  // Warm on demand: the feed's warm plan raises warmLevel; first non-'none' level attaches src.
  // P2 escalation (design 048 §5.1): metadata -> auto on an element that already has a src sets
  // the attribute; if 800ms later no progress has arrived and readyState < 3, fall back to
  // load() — but NEVER while inView (load() would reset playback). Worst case it re-fetches moov.
  useEffect(() => {
    warmLevelRef.current = warmLevel;
    if (warmLevel === 'none') {
      // P2.1 metrics finding: an in-flight full-file download on a slide that has DROPPED OUT of
      // the warm plan keeps saturating the link for slides the user is actually on (Safari pulls
      // whole files under preload='auto' and nothing ever stopped them — 22MB videos measured at
      // 22-118s to first frame purely from this). Sanctioned abort: after a 2s debounce (so plan
      // rebuilds that re-warm ±1 within 1.5s never trigger it), if this slide is still out of
      // plan, off-screen, and ACTIVELY TRANSFERRING (networkState 2), detach its src. Its buffer
      // is sacrificed — acceptable for a slide the user moved past — and the src re-attaches
      // through the normal warm/activation paths if they return.
      // Pass-through tuning (metrics: a 15-slide browse-burst left a trail of just-started
      // full-file pulls — incl. 366MB/131MB ads — each enjoying the full 2s grace, choking the
      // eventual landing to ~20s): a slide that was barely watched (<2s in) with no meaningful
      // buffer dies in 700ms; the 2s grace remains for real ex-neighbors the plan may re-warm.
      const el0 = videoRef.current;
      const passThrough = el0 && el0.currentTime < 2 && el0.readyState < 4;
      const t = setTimeout(() => {
        const el2 = videoRef.current;
        if (!el2 || inViewRef.current || warmLevelRef.current !== 'none') return;
        if (!srcAttachedRef.current || el2.networkState !== 2) return;  // only kill active transfers
        try {
          el2.removeAttribute('src');
          el2.load();  // commits the detach — aborts the fetch
        } catch { /* best-effort */ }
        srcAttachedRef.current = false;
        attachedLevelRef.current = null;
      }, passThrough ? 700 : 2000);
      return () => clearTimeout(t);
    }
    const el = videoRef.current;
    if (!srcAttachedRef.current) {
      ensureSrc();
      return;
    }
    if (warmLevel === 'auto' && attachedLevelRef.current === 'metadata' && el) {
      attachedLevelRef.current = 'auto';
      el.setAttribute('preload', 'auto');
      const marker = lastProgressAtRef.current;
      const t = setTimeout(() => {
        const v = videoRef.current;
        if (v && !inViewRef.current && v.readyState < 3 && lastProgressAtRef.current === marker) {
          try { v.load(); } catch { /* best-effort */ }
        }
      }, 800);
      return () => clearTimeout(t);
    }
  }, [warmLevel, ensureSrc]);
  
  // Initialize robust view tracking that never blocks user experience
  const { trackVideoCompletion } = useViewTracking(video, isPlaying, inView, publicationId);

  // Fire ad impression when an agency video enters the viewport (once per mount)
  const { instance, accounts } = useMsal();
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (!inView || !video.agency_video_id || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    (async () => {
      try {
        if (accounts.length === 0) return;
        const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        await trackAdImpression(video.agency_video_id, result.idToken, { event_type: 'impression', publication_id: publicationId || '' });
      } catch (e) {
        // silent — never surface to user
      }
    })();
  }, [inView, video.agency_video_id, instance, accounts, publicationId]);

  // TikTok-style autoplay: muted by default on mobile, unmuted once session unlocked
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!inView || !autoPlay) {
      playAttemptRef.current += 1;  // invalidate any pending play retry — see the race note below
      if (!videoEl.ended && videoEl.currentTime > 0) {
        saveResumePosition(video.id, videoEl.currentTime);
      }
      videoEl.pause();
      videoEl.muted = true;
      setIsPlaying(false);
      setIsMuted(true);
      setIsBuffering(false);
      return;
    }

    // Design 048 review HIGH: if an off-screen warm failed, the element holds a dead error state
    // and its buffer is already gone — reattach a FRESH src so the visible slide can recover
    // (this is the one sanctioned src reassignment; there is no buffer left to destroy).
    if (warmFailedRef.current) {
      warmFailedRef.current = false;
      srcAttachedRef.current = false;
    }
    ensureSrc();  // design 048: ordering can never leave the active slide without a source

    // Only SEEK when there is a real resume position: any currentTime assignment (even = 0)
    // clears the element's show-poster flag per spec, discarding the poster in every browser
    // before the viewer can see it (design 048 P2 fix). Elements start at 0 anyway.
    const resumeTime = getResumePosition(video.id);
    if (resumeTime > 0) {
      videoEl.currentTime = resumeTime;
    }
    lastSavedRef.current = videoEl.currentTime || 0;
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('webkit-playsinline', '');
    videoEl.removeAttribute('controls');
    videoEl.controls = false;

    const isMobile = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    const sessionAudioUnlocked = sessionStorage.getItem('mobile_audio_unlocked') === 'true';
    const startMuted = isMobile && !sessionAudioUnlocked;

    videoEl.muted = startMuted;
    setIsMuted(startMuted);

    // OFF-SCREEN RESURRECTION FIX (metrics-caught): play() is async and can stay pending for
    // seconds on a cold load. If the user swipes away, pause() interrupts it, the promise
    // rejects, and the retry below would re-play the video OFF-SCREEN — a hidden bandwidth hog
    // that can even run to a phantom completion (points credited for an ad nobody is watching:
    // measured live as exit events with pct=100 and 2 minutes of off-screen wall-clock).
    // Every retry/settle now checks the attempt token, which the !inView branch invalidates.
    const attempt = ++playAttemptRef.current;
    const stillActive = () => playAttemptRef.current === attempt && videoRef.current === videoEl;

    videoEl.play()
      .then(() => { if (stillActive()) setIsPlaying(true); })
      .catch(() => {
        if (!stillActive()) return;  // interrupted by a swipe-away — stay paused
        // Browser blocked — retry muted
        videoEl.muted = true;
        setIsMuted(true);
        return videoEl.play();
      })
      .then(() => { if (stillActive()) setIsPlaying(true); })
      .catch(() => { if (stillActive()) setIsPlaying(false); });

    if (video.id && !video.id.includes('.')) {
      viewVideo(video.id).catch(() => {});
    }
    // Defense-in-depth (review LOW): a slide can unmount straight from active on a >=3-slide
    // jump without an inView=false commit — invalidate pending retries and pause on teardown.
    return () => {
      playAttemptRef.current += 1;
      videoEl.pause();
    };
  }, [inView, autoPlay, video.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Show "tap to unmute" banner whenever the video is playing but muted on a touch device
  useEffect(() => {
    const isMobile = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isMobile && isPlaying && isMuted) {
      setShowTapToUnmute(true);
    } else {
      setShowTapToUnmute(false);
    }
  }, [isPlaying, isMuted]);
  
  // Auto-hide tap-to-unmute after 10 seconds
  useEffect(() => {
    if (!showTapToUnmute || !isPlaying) return;
    const timer = setTimeout(() => setShowTapToUnmute(false), 10000);
    return () => clearTimeout(timer);
  }, [showTapToUnmute, isPlaying]);
  
  // Handle tap to unmute — TikTok style session-based unlock
  const handleTapToUnmute = useCallback(async () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      setIsMuted(false);
      setShowTapToUnmute(false);
      sessionStorage.setItem('mobile_audio_unlocked', 'true');
      if (typeof unlockAudioContext === 'function') {
        unlockAudioContext().catch(() => {});
      }
    }
  }, [unlockAudioContext]);

  // Progress tracking — stable callback (no deps), durationRef prevents stale closures
  const updateProgress = useCallback(() => {
    if (!videoRef.current) return;
    const currentTime = videoRef.current.currentTime;
    const videoDuration = videoRef.current.duration;
    if (videoDuration && videoDuration > 0 && !isNaN(videoDuration) && !isNaN(currentTime)) {
      setProgress(Math.min(100, Math.max(0, (currentTime / videoDuration) * 100)));
      const roundedDuration = Math.round(videoDuration);
      if (roundedDuration !== durationRef.current && roundedDuration > 0) {
        durationRef.current = roundedDuration;
        setDuration(roundedDuration);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTimeUpdate = useCallback(() => {
    // Always update progress on every timeupdate event
    updateProgress();
    reportWarmState(); // design 048: ~4x/s buffer-health signal while playing

    // Save playback position for session resume (throttled to 0.5s deltas)
    const currentT = videoRef.current?.currentTime || 0;
    if (currentT > 0 && Math.abs(currentT - lastSavedRef.current) >= 0.5) {
      saveResumePosition(video.id, currentT);
      lastSavedRef.current = currentT;
    }
    
    // Check if video has ended
    if (videoRef.current && 
        videoRef.current.duration > 0 && 
        videoRef.current.currentTime > 1 && 
        videoRef.current.currentTime >= videoRef.current.duration - 0.5 && 
        !pointsDeducted && 
        !videoEnded) {
      
      // Track video completion for analytics
      trackVideoCompletion(videoRef.current.currentTime, videoRef.current.duration);

      // Fire ad completion event for agency videos
      if (video.agency_video_id && accounts.length > 0) {
        (async () => {
          try {
            const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
            await trackAdImpression(video.agency_video_id, result.idToken, { event_type: 'completion', publication_id: publicationId || '' });
          } catch { /* silent */ }
        })();
      }

      clearResumePosition(video.id);

      if (video.points) {
        setPointsDeducted(true);
        setVideoEnded(true);
        deductPoints(video.points, video.id);
        
        setShowPointsConfirmation(true);
        setPointsConfirmationVisible(true);
        
        setTimeout(() => {
          setPointsConfirmationVisible(false);
          setTimeout(() => setShowPointsConfirmation(false), 500);
        }, 3000);
        
        // Removed auto-scroll - let user control navigation
      }
    }
  }, [updateProgress, reportWarmState, video.points, video.id, video.agency_video_id, deductPoints, pointsDeducted, videoEnded, trackVideoCompletion, accounts, instance, publicationId]);

  const togglePlay = async () => {
    if (videoRef.current) {
      // Tapping while muted on mobile unmutes instead of toggling play
      if (showTapToUnmute) {
        await handleTapToUnmute();
        return;
      }
      
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleLike = async () => {
    if (!liked) {
      try {
        if (!video.id.includes('.')) {
          const result = await likeVideo(video.id);
          if (result?.likes) {
            setLikes(result.likes);
          } else {
            setLikes(likes + 1);
          }
        } else {
          setLikes(likes + 1);
        }
        setLiked(true);
      } catch (error) {
        console.error('Error liking video:', error);
        setLikes(likes + 1);
        setLiked(true);
      }
    }
  };

  // Format seconds as MM:SS countdown (remaining time)
  const formatCountdown = (dur, prog) => {
    if (!dur || dur <= 0) return '';
    const elapsed = (prog / 100) * dur;
    const remaining = Math.max(0, Math.ceil(dur - elapsed));
    const m = Math.floor(remaining / 60).toString().padStart(2, '0');
    const s = (remaining % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <VideoContainer>
      {/* Top bar — video details above video */}
      <TopBar>
        <VideoInfo>
          <VideoTitleRow>
            <VideoTitle>{video.title}</VideoTitle>
          </VideoTitleRow>
          <StatsRow>
            <VideoStats>
              <StatItem>👁️ {views.toLocaleString()}</StatItem>
              <StatItem>❤️ {likes.toLocaleString()}</StatItem>
            </VideoStats>
            {video.points > 0 && (
              <PointsCost>-{video.points} pts</PointsCost>
            )}
          </StatsRow>
          {(video.advertiser_url || video.source_url) && (
            <VideoUrl
              href={video.advertiser_url || video.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={video.agency_video_id && accounts.length > 0 ? () => {
                (async () => {
                  try {
                    const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
                    await trackAdImpression(video.agency_video_id, result.idToken, { event_type: 'click', publication_id: publicationId || '' });
                  } catch { /* silent */ }
                })();
              } : undefined}
            >
              <FaExternalLinkAlt size={10} />
              {(() => { try { return new URL(video.advertiser_url || video.source_url).hostname.replace(/^www\./, ''); } catch { return video.advertiser_url || video.source_url; } })()}
            </VideoUrl>
          )}
        </VideoInfo>
      </TopBar>

      <VideoArea>
      <Video
        ref={videoRef}
        poster={video.thumbnail_url || undefined}
        playsInline
        muted
        controls={false}
        onClick={togglePlay}
        onLoadedData={() => {
          if (videoRef.current?.duration) {
            setDuration(Math.round(videoRef.current.duration));
          }
          reportWarmState();
        }}
        onProgress={() => { lastProgressAtRef.current = performance.now(); reportWarmState(); }}
        onSuspend={reportWarmState}
        onWaiting={() => setIsBuffering(true)}
        onPlaying={() => {
          setIsPlaying(true); setIsBuffering(false); setHasPlayedOnce(true);
          // Design 048 P2: report time-to-first-frame once per visibility (best-effort analytics)
          if (inView && !startupSentRef.current && inViewSinceRef.current > 0) {
            startupSentRef.current = true;
            const ms = Math.round(performance.now() - inViewSinceRef.current);
            if (ms >= 0 && ms < 120000) {
              viewVideo(video.id, {
                eventType: 'startup', startup_ms: ms,
                publication_id: publicationId || '',
              }).catch(() => {});
            }
          }
        }}
        onCanPlay={() => { setIsBuffering(false); reportWarmState(); }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onVolumeChange={() => {
          if (videoRef.current) {
            const actualMuted = videoRef.current.muted;
            if (actualMuted !== isMuted) setIsMuted(actualMuted);
          }
        }}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          if (videoRef.current?.duration) {
            const videoDuration = Math.round(videoRef.current.duration);
            durationRef.current = videoDuration;
            setDuration(videoDuration);
          }
        }}
        onDurationChange={() => {
          if (videoRef.current?.duration) {
            const videoDuration = Math.round(videoRef.current.duration);
            durationRef.current = videoDuration;
            setDuration(videoDuration);
          }
        }}
        onEnded={() => clearResumePosition(video.id)}
        onError={(e) => {
          // Design 048 §4.1 (security): NEVER credit points or scroll the feed for an
          // OFF-SCREEN slide — a failed background preload must be inert. Mark it so the
          // autoplay path retries with a fresh src when the user actually arrives (review HIGH).
          if (!inView) {
            setIsBuffering(false);
            warmFailedRef.current = true;
            return;
          }
          setIsBuffering(false);
          setHasPlayedOnce(true); // stop the initial spinner — error state, not loading
          clearResumePosition(video.id);
          if (videoEnded || pointsDeducted) return;
          
          const error = e.target.error;
          console.error('Video error:', error);
          
          if (!pointsDeducted && !videoEnded && video.points) {
            setPointsDeducted(true);
            setVideoEnded(true);
            deductPoints(video.points, video.id);
            
            setShowPointsConfirmation(true);
            setPointsConfirmationVisible(true);
            
            setTimeout(() => {
              setPointsConfirmationVisible(false);
              setTimeout(() => setShowPointsConfirmation(false), 500);
            }, 3000);
            
            if (onVideoComplete) {
              setTimeout(() => onVideoComplete(), 2000);
            }
          }
        }}
      />

      {inView && (isBuffering || !hasPlayedOnce) && <BufferingSpinner />}

      {/* Right-side controls float over the video area */}
      <Controls>
        <ControlButton onClick={togglePlay}>
          {isPlaying ? <FaPause /> : <FaPlay />}
        </ControlButton>
        <ControlButton onClick={toggleMute}>
          {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
        </ControlButton>
        <LikeButton liked={liked} onClick={handleLike}>
          <FaHeart />
        </LikeButton>
      </Controls>
    </VideoArea>

    {/* Bottom bar — countdown + progress bar */}
    <BottomBar>
      <DurationRow>
        {formatCountdown(duration, progress)}
      </DurationRow>
      <ProgressBar>
        <Progress progress={progress} style={{ width: `${progress}%` }} />
      </ProgressBar>
    </BottomBar>

    {/* Points confirmation popup — fixed overlay, outside layout flow */}
    {showPointsConfirmation && (
      <div style={{ 
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 2000, pointerEvents: 'none'
      }}>
        <div style={{ width: '250px', maxWidth: '80%' }}>
          <PointsConfirmation isVisible={pointsConfirmationVisible} display={true}>
            <PointsMessage>Points deducted!</PointsMessage>
            <PointsAmount>-{video.points} points</PointsAmount>
            <PointsMessage>Thank you for watching</PointsMessage>
          </PointsConfirmation>
        </div>
      </div>
    )}

    {/* Tap to Unmute Message */}
    {showTapToUnmute && (
      <TapToUnmuteMessage onClick={handleTapToUnmute}>
        <SoundIcon />
        Tap to unmute
      </TapToUnmuteMessage>
    )}
    </VideoContainer>
  );
};

export default VideoPlayer;
